//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { useQuery } from '@tanstack/react-query';

import { SamplingCatalogProductsResponse } from '@apis/campaign-requests/sampling/products/samplingProductsApiModel';
import {
  searchBvCatalogProducts,
  searchCuralateCatalogProducts,
} from '@apis/universal-image-actions/universalImageActionsApi';
import {
  CuralateCatalogProduct,
  CuralateCatalogProducts,
} from '@apis/universal-image-actions/universalImageActionsApiModel';
import ClientId from '@models/ClientId';
import PartialCuid from '@models/PartialCuid';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { FIVE_MINUTES_STALE_TIME } from '@queries/QueryOptionsConstants';
import { isDefined, isTruthy } from '@utils/filterPredicates';

const mapBVResponseToCuralateProducts = ({
  products,
}: SamplingCatalogProductsResponse): CuralateCatalogProduct[] => {
  if (isDefined(products)) {
    return products.reduce<CuralateCatalogProduct[]>(
      (formattedProducts, product) => {
        if (isDefined(product.styleId) && !!product.productDetailPageUrl) {
          formattedProducts.push({
            destinationUrl: product.productDetailPageUrl,
            images: [
              {
                fileId: product.fileId,
                imageUrl: product.imageUrl,
                sourceImageUrl: product.imageUrl,
              },
            ],
            name: product.name,
            productStyleId: product.styleId,
            styleId: product.styleId,
            description: product.description,
            externalId: product.samplingProductId,
          });
        }

        return formattedProducts;
      },
      [],
    );
  }
  return [];
};

export interface ProductsParams {
  hasCatalog: boolean;
  isBvCampaignTagged?: boolean;
  isEnabled: boolean;
  keywords: string;
  partialCuid: PartialCuid | undefined;
  productSuggestionBoxId: string;
  limit: number;
  offset: number;
  catalogClientId?: ClientId;
  getCatalogSuggestions: () => CuralateCatalogProduct[];
}

export const useProductsQuery = ({
  catalogClientId,
  getCatalogSuggestions,
  hasCatalog,
  isBvCampaignTagged = false,
  isEnabled,
  keywords,
  limit,
  offset,
  partialCuid,
  productSuggestionBoxId,
}: ProductsParams) =>
  useQuery<CuralateCatalogProducts>({
    enabled: isEnabled && hasCatalog && isDefined(partialCuid), // TODO: PD-282907 - Remove isEnabled when the UIA Products Api Port feature flag is removed
    queryKey: [
      QueryKeyConstants.UniversalImageActions,
      QueryKeyConstants.UniversalImageActionsProducts,
      QueryKeyConstants.UniversalImageActionsProductsList,
      {
        isBvCampaignTagged,
        keywords,
        partialCuid,
        limit,
        offset,
        productSuggestionBoxId,
      },
    ],
    queryFn: async ({ signal }) => {
      if (!isDefined(partialCuid)) {
        throw new Error('PartialCuid is required to fetch products');
      }

      // If we have search text present i.e. keywords, we perform a search for the CuralateCatalogProducts,
      // otherwise we return the catalog suggestions
      if (isTruthy(keywords)) {
        if (isBvCampaignTagged) {
          const res = await searchBvCatalogProducts({ keywords }, { signal });
          return {
            products: mapBVResponseToCuralateProducts(res),
            totalCount: res.total,
          };
        } else {
          return await searchCuralateCatalogProducts(
            { keywords, partialCuid, limit, offset, catalogClientId },
            { signal },
          );
        }
      } else {
        const products = getCatalogSuggestions();
        return {
          products,
          totalCount: products.length,
          isCatalogSuggestions: true,
        };
      }
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
